import React from "react";

import TableCreatorDragButton from "@FillInTableContainer/shared/components/TableCreatorDragButton/TableCreatorDragButton";
import { VALUE_CONTAINER_DRAGGING_SHELL_KEYS } from "@FillInTableContainer/shared/constants/isActiveDragging.constants";
import { useTableCreatorFocusStatus } from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { GroupTableKeyType } from "@FillInTableContainer/shared/types/tableCreator.types";

import * as S from "./ValueDragButton.style";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: () => void;
  handleDragOn: () => void;
  isMultipleLastValue: boolean;
  isFocused: boolean;
  positionInfo: {
    groupTableKey: GroupTableKeyType;
    groupTableIndex: number;
    tableKey: string | null;
    tableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number;
    subGroupShellIndex: number | null;
    subGroupShellKey: string | null;
    shellKey: string;
    shellIndex: number;
    valueIndex: number;
  };
}

const ValueDragButton = ({
  handleDragEnd,
  handleDragOn,
  handleDragStart,
  isFocused,
  isMultipleLastValue,
  positionInfo,
  ...rest
}: Props) => {
  const { changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();

  const { valueIndex, shellKey } = positionInfo;

  const changePositionInfo = () => {
    changeInfo({ ...positionInfo });
  };

  const changePositionInfoToDefault = () => {
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const changeFocusStatusForDragging = () => {
    changePositionInfo();
    changeFocusStatus({
      type: "dragging",
      container: "shell",
    });
  };

  const changeFocusStatusForDragEnd = () => {
    changePositionInfoToDefault();
    changeFocusStatus({
      type: "default",
      container: "noFocus",
    });
  };

  if (
    isMultipleLastValue &&
    !VALUE_CONTAINER_DRAGGING_SHELL_KEYS.includes(shellKey)
  )
    return <S.HiddenBox className="value-drag-button" />;

  return (
    <S.DragButton
      draggable
      onClick={(e) => {
        e.stopPropagation();
        changeFocusStatusForDragging();
      }}
      onDragStart={(e) => {
        handleDragStart(e, valueIndex);
        changeFocusStatusForDragging();
      }}
      onDragEnd={() => {
        if (changeFocusStatusForDragEnd) {
          handleDragEnd();
          changeFocusStatusForDragEnd();
        }
      }}
      onDrag={handleDragOn}
      {...rest}
    >
      <TableCreatorDragButton
        dragContainer="value"
        isDragging={isFocused && focusStatus.type === "dragging"}
        onClick={(e) => {
          e.stopPropagation();
          changeFocusStatusForDragging();
        }}
      />
    </S.DragButton>
  );
};

export default ValueDragButton;
